// Defines the Store Action domain model and the transport contract shared by the API and query layers.
export type StoreActionStatus = 'ACTIVE' | 'COMPLETED';

export const STORE_ACTION_PAGE_SIZE = 20;

export const STORE_ACTION_WIRE_STATUS = {
  active: 'active',
  completed: 'completed',
} as const;

export type StoreActionWireStatus =
  typeof STORE_ACTION_WIRE_STATUS[keyof typeof STORE_ACTION_WIRE_STATUS];

export const hasSuccessfulStoreActionStatus = (
  response: unknown
): response is { readonly status: 200 } =>
  Boolean(
    response &&
      typeof response === 'object' &&
      (response as { readonly status?: unknown }).status === 200
  );

export type CompactStoreActionCategory =
  | 'ALL'
  | 'AGR'
  | 'INV'
  | 'CUS'
  | 'FACTPAY'
  | 'OTHER';

export interface StoreAction {
  readonly storeActionItemId: string | number | null;
  readonly storeActionType: string;
  readonly storeActionCategory: string;
  readonly description: string;
  readonly status: StoreActionStatus;
  readonly active: boolean;
  readonly createdAt: string;
  readonly redirectUrl: string;
  readonly storeId?: string | number;
  readonly agreementId?: string | number;
  readonly customerId?: string | number;
  readonly inventoryId?: string | number;
  readonly externalIdentifier?: string | number;
  readonly isVirtual?: boolean;
}

export interface StoreActionPageRequest {
  readonly storeNumber: string;
  readonly status: StoreActionWireStatus;
  readonly offset: number;
  readonly limit: number;
}

export interface StoreActionPage {
  readonly items: ReadonlyArray<StoreAction>;
  readonly offset: number;
  readonly limit: number;
  readonly nextOffset: number | null;
  readonly total: number;
  readonly countsByCategory: Readonly<Record<string, number>>;
}

export interface StoreActionTransportItem {
  readonly storeActionItemId: string | number | null;
  readonly storeId?: string | number;
  readonly redirectUrl: string;
  readonly customerId?: string | number;
  readonly agreementId?: string | number;
  readonly inventoryId?: string | number;
  readonly storeActionCategory: string;
  readonly storeActionType: string;
  readonly createdDate: string;
  readonly description: string;
  readonly externalIdentifier?: string | number;
  readonly active: boolean;
}

export interface StoreActionTransportResponse {
  readonly status: number;
  readonly data: {
    readonly storeActionDetails: ReadonlyArray<StoreActionTransportItem>;
    readonly totalCount: number;
    readonly counts: ReadonlyArray<{
      readonly category: string;
      readonly count: number;
    }>;
  };
}

export interface InventoryTransferDetails {
  readonly inventoryHeader?: {
    readonly itemNumber?: string | number;
  };
  readonly inventoryInformation?: {
    readonly inventoryId?: string | number;
    readonly modelNumber?: string;
    readonly vendor?: string;
    readonly brand?: string;
    readonly serialNumber?: string;
  };
  readonly currentAgreement?: {
    readonly customerName?: string;
  };
  readonly inventoryTransferInformation?: {
    readonly receivingStoreNumber?: string | number;
    readonly receivingStoreCity?: string;
    readonly receivingStoreState?: string;
    readonly transferringStoreNumber?: string | number;
    readonly transferringStoreCity?: string;
    readonly transferringStoreState?: string;
  };
}

export const normalizeStoreActionStatus = (
  active: boolean
): StoreActionStatus => (active ? 'ACTIVE' : 'COMPLETED');
